import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import "./LivePageGridView.css";
import "./LiveViewPageListView.css";
import { motion } from "framer-motion";
import GridDropDown from "./GridDropdown.tsx";
import CustomVideoPlayer from "./PlayerWithTimeline.tsx";
import { useMediaQuery } from "@mui/material";
import { Dialog } from "primereact/dialog";
import LiveViewPageSkeleton from "./LiveViewPageSkeleton.tsx";
import Tooltip from "@mui/material/Tooltip";
import { useLiveViewDataContext } from "../../context/LiveViewDataContext.tsx";
import { useUserContext } from "../../context/UserContext.tsx";
import axios from "axios";
import { VideoJS, VideoJSHandle } from "../../components/VideoJS/VideoJS.tsx";
import {
  getCameraStatus,
  destroyAllConnections,
} from "../utils/video/videoConnection.tsx";
import FishEyeIcon from "./FishEyeIcon.tsx";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

const LivePageGridViewUpdated = () => {
  const [gridDialogVisible, setGridDialogVisible] = React.useState(false);
  const { setVideoData, videoHeader, setVideoHeader } =
    useLiveViewDataContext();
  const [cameraDataLoading, setCameraDataLoading] = useState(false);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isMediumScreen = useMediaQuery(
    "(min-width:601px) and (max-width:1200px)",
  );
  const [gridSize, setGridSize] = useState("3x3");
  const [cameras, setcameras] = useState<any[]>([]);
  const [newSrc, setNewSrc] = useState("");
  const [newHighSrc, setNewHighSrc] = useState("");
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [isFisheye, setIsFisheye] = useState(false);
  const [gridPTZMap, setGridPTZMap] = useState<Record<string, boolean>>({});
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const videoJsRefs = useRef<Record<string, VideoJSHandle | null>>({});
  const [fisheyeActiveMap, setFisheyeActiveMap] = useState<
    Record<string, boolean>
  >({});
  const [cameraLoading, setCameraLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [cameraStatuses, setCameraStatuses] = useState<Record<string, string>>(
    {},
  );
  // Suppress "connecting" flash after modal close for cameras that were already connected
  const everConnectedRef = useRef<Record<string, boolean>>({});
  // Stable ref to previous statuses — avoids setState when nothing changed
  const prevStatusesRef = useRef<Record<string, string>>({});

  const {
    globalstorevalue,
    timeZone,
    activeRemoteUrl,
    storeInfoMap,
    posCamera,
  } = useUserContext();

  // ── Init camera loading state when cameras list arrives ──
  useEffect(() => {
    if (!cameras.length) return;
    const initial: Record<string, boolean> = {};
    cameras.forEach((cam) => {
      initial[cam._id] = true;
    });
    setCameraLoading(initial);
  }, [cameras]);

  // ── Destroy all connections on unmount ──
  useEffect(() => {
    return () => {
      destroyAllConnections();
    };
  }, []);

  // ── Poll camera statuses — only setState when something actually changed ──
  useEffect(() => {
    if (!cameras.length) return;
    const interval = setInterval(() => {
      const next: Record<string, string> = {};
      let changed = false;

      cameras.forEach((camera) => {
        const src = `${activeRemoteUrl}/${camera._id}/whep`;
        const raw = getCameraStatus(src);

        if (raw === "connected") everConnectedRef.current[src] = true;

        // Suppress brief "connecting" flash after modal close
        const status =
          everConnectedRef.current[src] && raw === "connecting"
            ? "connected"
            : raw;

        next[src] = status;
        if (prevStatusesRef.current[src] !== status) changed = true;
      });

      if (changed) {
        prevStatusesRef.current = next;
        setCameraStatuses(next);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [cameras, activeRemoteUrl]);

  // ── Fetch cameras ──
  useEffect(() => {
    const fetchCamerasData = async () => {
      try {
        setCameraDataLoading(true);
        if (!activeRemoteUrl) {
          setcameras([]);
          return;
        }
        const response = await axios.get(`${activeRemoteUrl}/v1/cameras/ids`);
        setcameras(response?.data?.success?.ids ?? []);
      } catch {
        setcameras([]);
      } finally {
        setCameraDataLoading(false);
      }
    };
    fetchCamerasData();
  }, [activeRemoteUrl, storeInfoMap]);

  // ── Grid layout helpers ──
  const getGridColumns = useCallback(() => {
    const [, cols] = gridSize.split("x").map(Number);
    return cols;
  }, [gridSize]);

  const getResponsiveGridColumns = useCallback(() => {
    if (isMobile) return 1;
    if (isMediumScreen) return 2;
    return getGridColumns();
  }, [isMobile, isMediumScreen, getGridColumns]);

  const handleGridSizeChange = useCallback((event: any) => {
    setGridSize(event.target.value);
  }, []);

  // ── Drag vs click ──
  const dragThreshold = 5;
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!startPosRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    if (dx > dragThreshold || dy > dragThreshold) isDraggingRef.current = true;
  }, []);

  const handleMouseUp = useCallback(() => {
    startPosRef.current = null;
  }, []);

  // ── Camera click ──
  const handleCameraClick = useCallback(
    (camera: any) => {
      if (!activeRemoteUrl || !camera?._id) return;

      try {
        if (camera?.siteName && camera?.name) {
          const storeName = `${camera.siteName} - ${camera.name}`;
          (window as any).dataLayer = (window as any).dataLayer || [];
          (window as any).dataLayer.push({
            event: "live_page_video_opened",
            storeName,
          });
        }
      } catch {}

      const lowSrc = `${activeRemoteUrl}/${camera._id}/whep`;
      const highSrc = `${activeRemoteUrl}/${camera._id}_high/whep`;
      setNewSrc(lowSrc);
      setNewHighSrc(highSrc);
      setSelectedCameraId(camera._id);
      setIsFisheye(camera?.fisheye ?? false);
      setVideoData(highSrc);
      setVideoHeader(`${camera?.siteName} - ${camera?.name}`);
      setGridDialogVisible(true);
    },
    [activeRemoteUrl, setVideoData, setVideoHeader],
  );

  const handleSafeCameraClick = useCallback(
    (camera: any) => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        return;
      }
      handleCameraClick(camera);
    },
    [handleCameraClick],
  );

  const handleFisheyeLiveClick = useCallback((cameraId: string) => {
    setFisheyeActiveMap((prev) => ({ ...prev, [cameraId]: !prev[cameraId] }));
    setGridPTZMap((prev) => ({ ...prev, [cameraId]: !prev[cameraId] }));
  }, []);

  // ── Stable per-camera callbacks — only recreated when cameras list changes ──
  const setIsLoadingCallbacks = useMemo(() => {
    const map: Record<string, (val: boolean) => void> = {};
    cameras.forEach((cam) => {
      const key = cam._id;
      map[key] = (val: boolean) =>
        setCameraLoading((prev) => {
          if (prev[key] === val) return prev; // skip if unchanged
          return { ...prev, [key]: val };
        });
    });
    return map;
  }, [cameras]);

  const onPlayingCallbacks = useMemo(() => {
    const map: Record<string, () => void> = {};
    cameras.forEach((cam) => {
      const key = cam._id;
      map[key] = () =>
        setCameraLoading((prev) => {
          if (prev[key] === false) return prev;
          return { ...prev, [key]: false };
        });
    });
    return map;
  }, [cameras]);

  // ── Dialog hide ──
  const handleDialogHide = useCallback(() => {
    setGridDialogVisible(false);
    setSelectedCameraId(null);
  }, []);

  // ── DataLayer on dialog open ──
  useEffect(() => {
    if (!gridDialogVisible || !videoHeader) return;
    try {
      const storeName = videoHeader.split(" -- ")[0].trim() || videoHeader;
      if (!storeName) return;
      const w = window as any;
      w.dataLayer = w.dataLayer || [];
      w.storeCodeCounts = w.storeCodeCounts || {};
      w.storeCodeCounts[storeName] = (w.storeCodeCounts[storeName] || 0) + 1;
      w.dataLayer.push({
        event: "live_page_video_opened",
        videoHeader: storeName,
        VideoStoreCodeCount: w.storeCodeCounts[storeName],
      });
      setTimeout(() => {
        w.dataLayer.push({
          videoHeader: undefined,
          VideoStoreCodeCount: undefined,
        });
      }, 500);
    } catch {}
  }, [gridDialogVisible, videoHeader]);

  // ── Camera grid — memoized so it only re-renders when its specific deps change ──
  const cameraGrid = useMemo(() => {
    if (!cameras.length) {
      return <div className="no-results-message">No cameras available</div>;
    }

    return cameras.map((camera) => {
      const cameraKey = camera._id;
      const src = `${activeRemoteUrl}/${cameraKey}/whep`;
      const status = cameraStatuses[src] ?? "connecting";

      return (
        <motion.div
          className="poster-container"
          key={cameraKey}
          style={{ height: "260px", position: "relative", cursor: "pointer" }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={() => handleSafeCameraClick(camera)}
        >
          {/* CONNECTING */}
          {status === "connecting" && (
            <div className="overlay center">
              <div className="spinner" />
              <span>Connecting…</span>
            </div>
          )}

          {/* DISCONNECTED */}
          {status === "disconnected" && (
            <div className="overlay center error">
              <span>Camera is Disconnected</span>
            </div>
          )}

          {/* LIVE BADGE */}
          <div
            className="absolute top-2.5 left-3 flex items-center gap-1"
            style={{
              zIndex: 5,
              background: "rgba(0,0,0,0.6)",
              borderRadius: "4px",
              padding: "3px 8px",
            }}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full inline-block mt-0.5 bg-danger`}
              style={{
                animation: "recordingPulse 2s infinite ease-in-out",
              }}
            />
            <span
              style={{
                color: "#fff",
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.05em",
              }}
            >
              LIVE
            </span>
          </div>

          {/* BOTTOM INFO */}
          <div className="storename-info-container">
            <div className="storename-info-left-container">
              <div className="status-badge connected">{camera.name}</div>
            </div>
            <div className="storename-info-right-container">
              <span
                style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px" }}
              >
                {camera.siteName}
              </span>
              {camera?.fisheye && (
                <div>
                  <FishEyeIcon
                    className={`fisheye-icon ${fisheyeActiveMap[cameraKey] ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFisheyeLiveClick(cameraKey);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="poster">
            <TransformWrapper
              disabled={!!camera?.fisheye}
              wheel={{ disabled: false }}
            >
              <TransformComponent>
                <VideoJS
                  src={src}
                  ref={(el) => {
                    videoJsRefs.current[cameraKey] = el;
                  }}
                  videoRef={{
                    get current() {
                      return videoRefs.current[cameraKey] ?? null;
                    },
                    set current(val) {
                      videoRefs.current[cameraKey] = val;
                    },
                  }}
                  isLive={true}
                  muted={true}
                  cameraId={cameraKey}
                  splitView={false}
                  ptzOn={Boolean(gridPTZMap[cameraKey] && camera?.fisheye)}
                  fisheye={camera?.fisheye}
                  isLoading={cameraLoading[cameraKey]}
                  setIsLoading={setIsLoadingCallbacks[cameraKey]}
                  onPlaying={onPlayingCallbacks[cameraKey]}
                />
              </TransformComponent>
            </TransformWrapper>
          </div>
        </motion.div>
      );
    });
  }, [
    cameras,
    activeRemoteUrl,
    cameraStatuses,
    fisheyeActiveMap,
    gridPTZMap,
    cameraLoading,
    setIsLoadingCallbacks,
    onPlayingCallbacks,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleSafeCameraClick,
    handleFisheyeLiveClick,
  ]);

  return (
    <>
      <Dialog
        header={videoHeader}
        visible={gridDialogVisible}
        maximized
        style={{ width: "100vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        onHide={handleDialogHide}
      >
        <div className="dialog-container">
          <div
            className="video-modal-container w-full"
            style={{ height: "100vh", backgroundColor: "black" }}
          >
            <CustomVideoPlayer
              src={newSrc}
              highSrc={newHighSrc}
              cameraId={selectedCameraId}
              isFisheye={isFisheye}
              timeZone={timeZone}
              remoteUrl={activeRemoteUrl}
              posCamera={posCamera}
            />
          </div>
        </div>
      </Dialog>

      <div className="liveview-page-MainContainer p-5 bg-[rgb(var(--surface-1))] rounded-md border border-[rgb(var(--border-primary))]">
        <div className="liveview-page-InnerContainer">
          <div className="LiveViewHeading">
            <div className="flex flex-col">
              <span className="page-breadcrumb">LIVE</span>
              <h1 className="card-title mt-2.5">Live View</h1>
              <p className="card-subtitle m-0 mt-1">
                Camera grid for real-time monitoring and customer viewing
              </p>
            </div>
            <div
              className="LiveViewHeadingRightContainer"
              style={{ gap: isMobile ? "0px" : "14px" }}
            >
              {!cameraDataLoading && (
                <Tooltip title="Grid View" placement="top">
                  <div
                    style={{
                      marginTop: "5px",
                      cursor: "pointer",
                      display: isMobile ? "none" : "flex",
                    }}
                  >
                    <GridDropDown
                      gridSize={gridSize}
                      handleGridSizeChange={handleGridSizeChange}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>

          {cameraDataLoading ? (
            <LiveViewPageSkeleton
              columns={
                isMobile ? 1 : isMediumScreen ? 2 : getResponsiveGridColumns()
              }
              count={isMobile ? 3 : isMediumScreen ? 4 : 9}
            />
          ) : !globalstorevalue ? (
            <div className="no-results-message">Please select a store</div>
          ) : cameras.length === 0 ? (
            <div className="no-results-message">
              No camera available for the selected store
            </div>
          ) : (
            <motion.div
              className="gridContainer"
              style={{
                gridTemplateColumns: `repeat(${getResponsiveGridColumns()}, 1fr)`,
              }}
              layout
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              {cameraGrid}
            </motion.div>
          )}
        </div>

        <style>{
          `
            .react-transform-wrapper, .react-transform-component {
              width: 100% !important;
              height: 100%  !important;
            }
          `
          }</style>
      </div>
    </>
  );
};

export default LivePageGridViewUpdated;
